import {
  PRESENTATION_CHART_TYPES,
  PRESENTATION_SLIDE_KINDS,
  resolveExportImage,
} from '@utilities/powerpoint';
import {
  mapTreeToExportItem,
  transformTreeNarrativeData,
} from '../../Report/components/Tab/components/Visualizations/utilities/tree_helpers';

const IMAGE_TYPES = new Set(['chart-image']);
const NATIVE_CHART_TYPES = new Set([
  'bar-chart',
  'bar-chart-two-labels',
  'bar-stack-chart',
  'column-chart',
]);
const NATIVE_TREE_TYPES = new Set(['brand-tree-v2', 'ebsova-tree', 'retailer-leakage-tree']);

const toNumber = (value) => {
  const resolved = value && typeof value === 'object' ? value.y : value;
  return typeof resolved === 'number' && Number.isFinite(resolved) ? resolved : null;
};

const toComparison = (value) => ({
  delta: typeof value?.yDelta === 'number' && Number.isFinite(value.yDelta) ? value.yDelta : null,
  value: typeof value?.y2 === 'number' && Number.isFinite(value.y2) ? value.y2 : null,
});

const toSeries = (series = []) =>
  series.map((item) => ({
    name: item.name || 'Series',
    values: (item.data || []).map(toNumber),
  }));

const toNativeChartSlide = ({ content, title, type }) => {
  const chart = content.data;
  const values = chart.series?.[0]?.data || [];
  return {
    categories: chart.xAxis?.categories || [],
    chartType:
      type === 'column-chart' ? PRESENTATION_CHART_TYPES.COLUMN : PRESENTATION_CHART_TYPES.BAR,
    comparisonValues: type === 'bar-chart-two-labels' ? values.map(toComparison) : undefined,
    insight: content.readAs || undefined,
    kind: PRESENTATION_SLIDE_KINDS.CHART,
    series: toSeries(chart.series),
    stacking: type === 'bar-stack-chart' ? 'percent' : undefined,
    title,
    valueFormat: chart.format || undefined,
  };
};

const toScatterSlide = ({ content, title }) => ({
  chartType: PRESENTATION_CHART_TYPES.SCATTER,
  insight: content.readAs || undefined,
  kind: PRESENTATION_SLIDE_KINDS.CHART,
  series: (content.data.series || []).map((series) => ({
    name: series.name || 'Series',
    values: (series.data || []).map((point) => ({
      label: point.name || '',
      x: point.x,
      y: point.y,
    })),
  })),
  title,
  valueFormat: content.data.formatY || undefined,
  xAxisTitle: content.data.nameX,
  xValueFormat: content.data.formatX || undefined,
  yAxisTitle: content.data.nameY,
});

const toTableSlide = ({ content, title }) => ({
  columns: (content.data.columns || []).map((column, index) => ({
    format: column.format,
    header: column.Header || column.header || `Column ${index + 1}`,
    key: column.accessor || column.accessorKey,
  })),
  insight: content.readAs || undefined,
  kind: PRESENTATION_SLIDE_KINDS.TABLE,
  rows: content.data.data || [],
  title,
});

const toTreeSlide = ({ content, title, type }) => {
  const transformedTree = transformTreeNarrativeData(content.data?.nodes || [], type);
  const tree = mapTreeToExportItem({
    title,
    treeNodes: transformedTree.cardBlocks?.treeNodes,
    type,
  });

  return {
    connectors: tree.connectors,
    insight: content.readAs || undefined,
    kind: PRESENTATION_SLIDE_KINDS.TREE,
    nodes: tree.nodes.map(({ nodeId, ...node }) => ({ ...node, id: nodeId })),
    title,
  };
};

const toImageSlide = async ({ content, resolveImage, title }) => {
  const resolved = await resolveImage(content.data);
  if (!resolved) return null;
  return {
    data: resolved.dataUrl,
    insight: content.readAs || undefined,
    kind: PRESENTATION_SLIDE_KINDS.IMAGE,
    placement: resolved.options,
    title,
  };
};

const toSlides = async (item, resolveImage) => {
  const title = item.title || 'Narrative topic';
  const contents = Array.isArray(item.content) ? item.content.filter(Boolean) : [];
  if (NATIVE_CHART_TYPES.has(item.type)) {
    return contents.map((content) => toNativeChartSlide({ content, title, type: item.type }));
  }
  if (item.type === 'scatter-chart') {
    return contents.map((content) => toScatterSlide({ content, title }));
  }
  if (item.type === 'table') {
    return contents.map((content) => toTableSlide({ content, title }));
  }
  if (NATIVE_TREE_TYPES.has(item.type)) {
    return contents.map((content) => toTreeSlide({ content, title, type: item.type }));
  }
  if (IMAGE_TYPES.has(item.type)) {
    return (
      await Promise.all(contents.map((content) => toImageSlide({ content, resolveImage, title })))
    ).filter(Boolean);
  }
  throw new TypeError(`Unsupported Narrative PowerPoint topic type: ${item.type}`);
};

export const buildNarrativePresentation = async ({
  exportData,
  fileName,
  resolveImage = resolveExportImage,
}) => {
  const slides = [];
  for (const item of exportData.data || []) {
    // Image capture is asynchronous and topic order must remain stable.
    // eslint-disable-next-line no-await-in-loop
    slides.push(...(await toSlides(item, resolveImage)));
  }

  return {
    fileName,
    footer: {
      attributes: exportData.shopperAttributes,
      dateRange: exportData.reportDateRange?.every(Boolean)
        ? exportData.reportDateRange
        : undefined,
      sourceLabel: exportData.reportType,
      sourceUrl: exportData.reportUrl,
    },
    sections: [{ id: 'narrative-topics', slides }],
    title: {
      heading: exportData.reportName || 'Narrative',
      subtitle: exportData.reportType || 'Narrative',
    },
  };
};
